import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Row, Col, Card, Spinner, Alert, Table,
  Button, Badge, ButtonGroup, Tabs, Tab, Stack,
} from 'react-bootstrap';
import { ShieldCheck, BarChart2, BookOpen, Bell, ClipboardList, Settings2 } from 'lucide-react';
import {
  getAllTenants, getAllProjects, getSystemAdminMetrics,
  getAuditLogs, getGlobalTaxonomies,
} from '../api/api';
import api from '../api/api';
import AppFooter from '../components/Common/AppFooter';
import TenantTable from '../components/SystemAdmin/TenantTable';
import ProjectStatusTable from '../components/SystemAdmin/ProjectStatusTable';
import KPICards from '../components/SystemAdmin/KPICards';
import TenantStatusSummary from '../components/SystemAdmin/TenantStatusSummary';
import ProjectStageSummary from '../components/SystemAdmin/ProjectStageSummary';
import AuditLogTable from '../components/SystemAdmin/AuditLogTable';
import DefineTaxonomyModal from '../components/SystemAdmin/DefineTaxonomyModal';
import RechargeWalletModal from '../components/SystemAdmin/RechargeWalletModal';
import AddReleaseNoteModal from '../components/SystemAdmin/AddReleaseNoteModal';
import ModuleAssignmentModal from '../components/SystemAdmin/ModuleAssignmentModal';
import SubscriptionAnalyticsPanel from '../components/SystemAdmin/SubscriptionAnalyticsPanel';
import { useDispatch } from 'react-redux';
import { addNotification } from '../redux/slices/userSlice';
import { showToast } from '../utils/toast';

const SystemAdminDashboard = () => {
  const dispatch = useDispatch();
  const [tenants, setTenants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modals
  const [showTaxonomyModal, setShowTaxonomyModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [newNote, setNewNote] = useState({
    version: '', category: 'Improvements', title: '', description: '', is_staging_only: false,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [tenantsRes, projectsRes, metricsRes, auditRes, taxonomyRes, notesRes] = await Promise.all([
        getAllTenants(), getAllProjects(), getSystemAdminMetrics(),
        getAuditLogs(20), getGlobalTaxonomies(), api.get('/public/release-notes'),
      ]);
      setTenants(tenantsRes.data);
      setProjects(projectsRes.data);
      setMetrics(metricsRes.data);
      setAuditLogs(auditRes.data);
      setTaxonomies(taxonomyRes.data);
      setReleaseNotes(notesRes.data);
    } catch {
      setError('Failed to load system data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadNotes = async () => {
    const res = await api.get('/public/release-notes');
    setReleaseNotes(res.data);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this release note?')) return;
    try {
      await api.delete(`/admin/release-notes/${noteId}`);
      loadNotes();
    } catch {
      showToast('Failed to delete note.', 'error');
    }
  };

  const handleSaveNote = async () => {
    try {
      if (editingNoteId) await api.put(`/admin/release-notes/${editingNoteId}`, newNote);
      else await api.post('/admin/release-notes', newNote);
      setShowNoteModal(false);
      setEditingNoteId(null);
      setNewNote({ version: '', category: 'Improvements', title: '', description: '', is_staging_only: false });
      loadNotes();
      dispatch(addNotification({ message: editingNoteId ? 'Note updated' : 'Note published', variant: 'success' }));
    } catch { /* noop */ }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted small fw-semibold">Synchronizing Global Governance…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100 d-flex flex-column">

      {/* ── Page Header ───────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg,#1e293b 0%,#334155 100%)', color: '#fff', padding: '28px 40px 24px' }}>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div style={{ background: 'rgba(255,255,255,.12)', borderRadius: 14, padding: 12 }}>
              <ShieldCheck size={28} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px' }}>
                System Administration
              </h1>
              <p style={{ margin: '2px 0 0', fontSize: 12, opacity: 0.65, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                Global Infrastructure &amp; Tenant Management
              </p>
            </div>
          </div>
          <Stack direction="horizontal" gap={2}>
            <Button variant="outline-light" size="sm" className="rounded-pill px-3" onClick={loadData}>
              🔄 Refresh
            </Button>
            <Badge bg="warning" text="dark" className="rounded-pill px-3 py-2" style={{ fontSize: 11, fontWeight: 700 }}>
              Admin Mode
            </Badge>
          </Stack>
        </div>

        {/* KPI strip inside header */}
        {metrics && (
          <div style={{ marginTop: 20 }}>
            <KPICards metrics={metrics} dark />
          </div>
        )}
      </div>

      <Container fluid className="py-4 px-4 px-lg-5 flex-grow-1">

        {error && <Alert variant="danger" className="shadow-sm rounded-3" role="alert">{error}</Alert>}

        {/* Summary charts */}
        {metrics && (
          <Row className="g-3 mb-4">
            <Col md={6}><TenantStatusSummary tenants={metrics.tenants} /></Col>
            <Col md={6}><ProjectStageSummary projects={metrics.projects} /></Col>
          </Row>
        )}

        {/* ── Main Tabs ─────────────────────────────────────────── */}
        <Tabs defaultActiveKey="tenants" className="mb-4" aria-label="System admin sections">

          {/* Tab 1: Tenants & Subscriptions */}
          <Tab eventKey="tenants" title={<span><Settings2 size={15} className="me-2" />Tenants &amp; Subscriptions</span>}>
            <Card className="border-0 shadow-sm rounded-4 mb-4">
              <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <span className="fw-bold small text-uppercase ls-1 text-primary">Tenant Registry</span>
                <div className="text-muted x-small">{tenants.length} tenants</div>
              </Card.Header>
              <Card.Body className="p-0">
                <TenantTable
                  tenants={tenants}
                  onRefresh={loadData}
                  onRecharge={(t) => { setSelectedTenant(t); setShowWalletModal(true); }}
                  onManageModules={(t) => { setSelectedTenant(t); setShowModuleModal(true); }}
                />
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-bottom py-3">
                <span className="fw-bold small text-uppercase ls-1 text-dark">Active Projects (Cross-Tenant)</span>
              </Card.Header>
              <Card.Body className="p-0">
                <ProjectStatusTable projects={projects} />
              </Card.Body>
            </Card>
          </Tab>

          {/* Tab 2: Subscription Analytics */}
          <Tab eventKey="analytics" title={<span><BarChart2 size={15} className="me-2" />Subscription Analytics</span>}>
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-bottom py-3">
                <span className="fw-bold small text-uppercase ls-1 text-primary">Credit Usage by Tenant</span>
              </Card.Header>
              <Card.Body className="p-4">
                <SubscriptionAnalyticsPanel />
              </Card.Body>
            </Card>
          </Tab>

          {/* Tab 3: Pedagogy Standards */}
          <Tab eventKey="standards" title={<span><BookOpen size={15} className="me-2" />Pedagogy Standards</span>}>
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <span className="fw-bold small text-uppercase ls-1 text-primary">Global Pedagogy Templates</span>
                <Button variant="primary" size="sm" className="rounded-pill" onClick={() => setShowTaxonomyModal(true)}>
                  + Define New Standard
                </Button>
              </Card.Header>
              <div style={{ overflowX: 'auto' }}>
                <Table striped hover responsive className="mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th>Standard Name</th>
                      <th>Hierarchy Structure</th>
                      <th>Reference Policy</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxonomies.map((tax) => (
                      <tr key={tax.id}>
                        <td className="fw-bold">{tax.name}</td>
                        <td className="small">
                          {tax.levels_config &&
                            Object.values(tax.levels_config)
                              .sort((a, b) => a.score - b.score)
                              .map((l) => l.name)
                              .join(' → ')}
                        </td>
                        <td>{tax.reference_policy?.citation_style || 'General OER'}</td>
                        <td>
                          <Badge bg={tax.is_active ? 'success' : 'secondary'}>
                            {tax.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>
          </Tab>

          {/* Tab 4: Release Notes */}
          <Tab eventKey="notes" title={<span><Bell size={15} className="me-2" />Release Notes</span>}>
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <span className="fw-bold small text-uppercase ls-1 text-primary">Product Release Notes</span>
                <Button variant="primary" size="sm" className="rounded-pill"
                  onClick={() => { setEditingNoteId(null); setShowNoteModal(true); }}>
                  + Add Note
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive className="small mb-0">
                  <thead className="table-light">
                    <tr><th>Version</th><th>Category</th><th>Title</th><th>Date</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {releaseNotes.length === 0 ? (
                      <tr><td colSpan={5} className="text-center text-muted py-4">No release notes yet.</td></tr>
                    ) : releaseNotes.map((note) => (
                      <tr key={note.id}>
                        <td><Badge bg="dark">{note.version}</Badge></td>
                        <td><Badge bg="info">{note.category}</Badge></td>
                        <td className="fw-bold">{note.title}</td>
                        <td>{new Date(note.release_date).toLocaleDateString()}</td>
                        <td>
                          <ButtonGroup size="sm">
                            <Button variant="outline-primary"
                              onClick={() => {
                                setEditingNoteId(note.id);
                                setNewNote({ version: note.version, category: note.category, title: note.title, description: note.description, is_staging_only: note.is_staging_only });
                                setShowNoteModal(true);
                              }}>✎</Button>
                            <Button variant="outline-danger" onClick={() => handleDeleteNote(note.id)}>🗑</Button>
                          </ButtonGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          {/* Tab 5: Audit Trail */}
          <Tab eventKey="audit" title={<span><ClipboardList size={15} className="me-2" />Audit Trail</span>}>
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-bottom py-3">
                <span className="fw-bold small text-uppercase ls-1 text-primary">System Audit Trail</span>
              </Card.Header>
              <Card.Body className="p-0">
                <AuditLogTable logs={auditLogs} />
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>

      {/* Modals */}
      <DefineTaxonomyModal show={showTaxonomyModal} onHide={() => setShowTaxonomyModal(false)} onCreated={loadData} />
      <RechargeWalletModal
        show={showWalletModal} tenant={selectedTenant}
        onHide={() => { setShowWalletModal(false); setSelectedTenant(null); }}
        onSuccess={() => { setShowWalletModal(false); setSelectedTenant(null); loadData(); }}
      />
      <AddReleaseNoteModal
        show={showNoteModal} onHide={() => setShowNoteModal(false)}
        noteData={newNote} setNoteData={setNewNote} onSave={handleSaveNote}
      />
      <ModuleAssignmentModal
        show={showModuleModal} tenant={selectedTenant}
        onHide={() => { setShowModuleModal(false); setSelectedTenant(null); }}
        onSaved={(modules) => {
          setTenants((prev) => prev.map((t) => t.id === selectedTenant?.id ? { ...t, modules } : t));
          setShowModuleModal(false); setSelectedTenant(null);
        }}
      />
      <AppFooter />
    </div>
  );
};

export default SystemAdminDashboard;
